import { IMPACTS, IMPACT_SUMMARY, NODES } from "./data";
import { StatusChip, Kicker, LocalBoundary } from "./ui";

export const ActImpact = ({ view, scenario }) => (
    <section data-testid="act-impact">
        <Kicker>Act 2 — Local impact assessment</Kicker>
        <h2 className="mt-2 text-2xl font-medium tracking-tight text-slate-900">
            Each organisation assesses the same change locally, in parallel
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
            Raw local evidence — installed-base records, network information, service
            schedules, quality policies — never leaves its owner. Each node returns a
            signed synthetic impact receipt with bounded findings only.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="impact-summary-strip">
            {IMPACT_SUMMARY.map((s) => (
                <StatusChip key={s.label} tone={s.tone} testId={`impact-summary-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                    {s.count} {s.label}
                </StatusChip>
            ))}
        </div>

        {scenario === "excluded" && (
            <div className="mt-4 rounded-sm border border-slate-300 bg-slate-100 p-4" data-testid="scenario-note-excluded">
                <p className="font-mono text-xs uppercase tracking-wide text-slate-600">
                    EXCLUDED — LEGACY DEVICE NOT SUPPORTED
                </p>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                    One legacy workstation cannot accept the update. It is held outside
                    the applicable change package rather than force-fitted into the
                    release. Its replacement decision remains a separate, locally owned
                    journey.
                </p>
            </div>
        )}
        {scenario === "denied" && (
            <div className="mt-4 rounded-sm border border-red-200 bg-red-50 p-4" data-testid="scenario-note-denied">
                <p className="font-mono text-xs uppercase tracking-wide text-red-700">
                    RED — PROPOSED DOWNTIME NOT ACCEPTABLE
                </p>
                <p className="mt-1 text-sm leading-relaxed text-red-900/70">
                    The Clinical Service Owner's red result materially changes the plan:
                    the common release date is withdrawn and, without an acceptable
                    clinical contingency, release at this site can be denied outright.
                </p>
            </div>
        )}

        <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="impact-grid">
            {Object.entries(IMPACTS).map(([id, imp]) => {
                const node = NODES.find((n) => n.id === id);
                const dim = view !== "network" && view !== "mfr" && view !== id;
                return (
                    <LocalBoundary key={id} testId={`impact-node-${id}`} dim={dim} highlight={view === id}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-medium text-slate-900">{node.name}</p>
                            <StatusChip tone={imp.tone} testId={`impact-result-${id}`}>{imp.result}</StatusChip>
                        </div>
                        <ul className="mt-3 space-y-1 text-sm text-slate-600">
                            {imp.findings.map((f) => <li key={f}>· {f}</li>)}
                        </ul>
                        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 border-t border-slate-100 pt-3 text-xs">
                            <Meta label="Local system" value={imp.meta.localSystem} />
                            <Meta label="Local policy version" value={imp.meta.policyVersion} />
                            <Meta label="Readiness state" value={imp.meta.readinessState} />
                            <Meta label="Unresolved questions" value={imp.meta.unresolvedQuestions} />
                        </dl>
                        <p className="mt-3 font-mono text-[10px] uppercase tracking-wide text-green-700">
                            Signed synthetic impact receipt returned
                        </p>
                    </LocalBoundary>
                );
            })}
        </div>

        <p className="mt-5 max-w-3xl text-xs leading-relaxed text-slate-500">
            Bounded findings only. Detailed network topology, clinical activity and
            proprietary supplier content are not exposed to other organisations. The
            final release cannot proceed as a single uncontrolled fleet update.
        </p>
    </section>
);

function Meta({ label, value }) {
    return (
        <div>
            <dt className="font-mono uppercase tracking-wide text-slate-400">{label}</dt>
            <dd className="mt-0.5 text-slate-700">{value}</dd>
        </div>
    );
}
